import type { FamilyChallengeProgress } from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createSignal } from 'solid-js';
import { formatFamilyChallengeDateRange } from '../lib/familyChallengePresentation';

type Props = {
  challenge: FamilyChallengeProgress | null | undefined;
  loading: boolean;
  onStart: (challengeName: string, targetDaysPerChild: number) => Promise<string | null>;
  onEnd: (familyChallengeId: number) => Promise<string | null>;
};

export const FamilyChallengeCard: Component<Props> = (props) => {
  const [editing, setEditing] = createSignal(false);
  const [confirmingEnd, setConfirmingEnd] = createSignal(false);
  const [challengeName, setChallengeName] = createSignal('Family high-five');
  const [targetDays, setTargetDays] = createSignal(3);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const complete = createMemo(() => {
    const challenge = props.challenge;
    return Boolean(
      challenge?.children.length &&
        challenge.children.every((child) => child.completedDays >= challenge.targetDaysPerChild),
    );
  });

  const start = async (event: Event) => {
    event.preventDefault();
    setError(null);
    const name = challengeName().trim();
    if (!name) {
      setError('Give the challenge a name.');
      return;
    }
    setBusy(true);
    const message = await props.onStart(name, targetDays());
    setBusy(false);
    if (message) {
      setError(message);
      return;
    }
    setEditing(false);
  };

  const end = async () => {
    const challenge = props.challenge;
    if (!challenge) return;
    setBusy(true);
    setError(null);
    const message = await props.onEnd(challenge.familyChallengeId);
    setBusy(false);
    if (message) {
      setError(message);
      return;
    }
    setConfirmingEnd(false);
  };

  return (
    <section class="home-card family-challenge-card" data-testid="family-challenge-card">
      <div class="child-dashboard-kicker">FAMILY CHALLENGE</div>
      <Show when={error()}>
        <div class="form-error" role="alert">
          {error()}
        </div>
      </Show>

      <Show when={!props.loading} fallback={<p>Loading the family challenge…</p>}>
        <Show
          when={!editing()}
          fallback={
            <form class="family-challenge-form" onSubmit={start}>
              <h2>
                {props.challenge
                  ? 'Start a new 7-day challenge'
                  : 'Set a family challenge for the next 7 days'}
              </h2>
              <Show when={props.challenge}>
                <p>
                  Starting a new challenge ends the current one immediately and replaces it for
                  every guardian and child.
                </p>
              </Show>
              <div class="field">
                <label class="field-label" for="family-challenge-name">
                  Challenge name
                </label>
                <input
                  id="family-challenge-name"
                  value={challengeName()}
                  maxlength="80"
                  disabled={busy()}
                  aria-describedby="family-challenge-name-hint"
                  onInput={(event) => setChallengeName(event.currentTarget.value)}
                />
                <span id="family-challenge-name-hint" class="field-hint">
                  Choose a short, fun name, such as “Team tidy-up”.
                </span>
              </div>
              <div class="field">
                <label class="field-label" for="family-challenge-target-days">
                  Days each child needs
                </label>
                <select
                  id="family-challenge-target-days"
                  value={targetDays()}
                  disabled={busy()}
                  onChange={(event) => setTargetDays(Number(event.currentTarget.value))}
                >
                  <For each={[1, 2, 3, 4, 5, 6, 7]}>
                    {(days) => (
                      <option value={days}>
                        {days} {days === 1 ? 'day' : 'days'} during the 7-day challenge
                      </option>
                    )}
                  </For>
                </select>
              </div>
              <div class="family-challenge-explainer">
                <p>
                  <strong>1.</strong> The challenge starts today and lasts 7 days.
                </p>
                <p>
                  <strong>2.</strong> A day counts for a child only after a guardian approves every
                  chore and routine due for that child that day. A day with no jobs cannot count.
                </p>
                <p>
                  <strong>3.</strong> The days do not need to be in a row, and each child can count
                  different days. The family completes the challenge when every child has enough
                  days.
                </p>
              </div>
              <div class="family-challenge-actions">
                <button type="submit" class="primary" disabled={busy()}>
                  {busy() ? 'Starting…' : 'Start seven-day challenge'}
                </button>
                <button
                  type="button"
                  class="ghost"
                  disabled={busy()}
                  onClick={() => {
                    setEditing(false);
                    setError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          }
        >
          <Show
            when={props.challenge}
            fallback={
              <>
                <h2>Ready for a family challenge?</h2>
                <p>
                  Choose how many days each child needs during the next 7 days. A day counts only
                  when all of that child's chores and routines due that day are approved.
                </p>
                <button
                  type="button"
                  class="primary"
                  onClick={() => setEditing(true)}
                  data-testid="start-family-challenge"
                >
                  Set up challenge
                </button>
              </>
            }
          >
            {(challenge) => (
              <>
                <h2>{challenge().challengeName}</h2>
                <Show
                  when={!complete()}
                  fallback={
                    <p>
                      Challenge complete! Every child reached {challenge().targetDaysPerChild} of{' '}
                      {challenge().targetDaysPerChild} days.
                    </p>
                  }
                >
                  <p>
                    <strong>Goal:</strong> each child needs {challenge().targetDaysPerChild}{' '}
                    {challenge().targetDaysPerChild === 1 ? 'day' : 'days'} to count before the
                    challenge ends.
                  </p>
                </Show>
                <p class="family-challenge-rule">
                  A day counts for a child only after a guardian approves every chore and routine
                  due for that child that day. A day with no jobs does not count. The days do not
                  need to be in a row, and children can count different days.
                </p>
                <p class="family-challenge-dates">
                  7-day challenge:{' '}
                  {formatFamilyChallengeDateRange(challenge().startsOn, challenge().endsOn)}
                </p>
                <For each={challenge().children}>
                  {(child) => {
                    const progress = () =>
                      Math.min(child.completedDays, challenge().targetDaysPerChild);
                    return (
                      <div class="family-challenge-row">
                        <span>{child.displayName}</span>
                        <progress
                          max={challenge().targetDaysPerChild}
                          value={progress()}
                          aria-label={`${child.displayName}: ${progress()} of ${challenge().targetDaysPerChild} days`}
                        />
                        <strong>
                          {progress()} of {challenge().targetDaysPerChild} days
                        </strong>
                      </div>
                    );
                  }}
                </For>
                <Show
                  when={!confirmingEnd()}
                  fallback={
                    <div class="family-challenge-end-confirm" role="alert">
                      <span>
                        End this challenge for the whole family? Its progress will stop showing for
                        every guardian and child.
                      </span>
                      <button type="button" class="danger" disabled={busy()} onClick={end}>
                        {busy() ? 'Ending…' : 'Yes, end it'}
                      </button>
                      <button
                        type="button"
                        class="ghost"
                        disabled={busy()}
                        onClick={() => setConfirmingEnd(false)}
                      >
                        Keep challenge
                      </button>
                    </div>
                  }
                >
                  <div class="family-challenge-actions">
                    <button type="button" class="ghost" onClick={() => setEditing(true)}>
                      New challenge
                    </button>
                    <button type="button" class="ghost" onClick={() => setConfirmingEnd(true)}>
                      End challenge
                    </button>
                  </div>
                </Show>
              </>
            )}
          </Show>
        </Show>
      </Show>
    </section>
  );
};
